import type { TEndpointMethod } from "~/shared/interfaces/api";
import type {ICatalogFiltersQuery,TCatalogSorting} from "~/shared/interfaces/catalog";

interface IEndpoints {
  cart: {
    getCartContent: TEndpointMethod;
    getCartPreview: TEndpointMethod;
  }
  order: {
    getDeliveryAddress: TEndpointMethod;
    getDeliveryCompanies: TEndpointMethod;
    getOrderProduct: TEndpointMethod<{
      query: {
        posIds: string[];
      };
    }>;
    getCityWarehouses: TEndpointMethod<{
      citySlug: string;
    }>;
  }
  search: {
    searchByArticle: TEndpointMethod<{
      article: string;
      query: {
        citySlug: string;
      };
    }>;
    searchByArticleAndBrand: TEndpointMethod<{
      article: string;
      brand: string;
      query: {
        citySlug: string;
      };
    }>;
    searchProduct: TEndpointMethod<{
      productId: string;
    }>;
  }
  catalog: {
    getInitialCategories: TEndpointMethod;
    getCategories: TEndpointMethod<{
      slug: string;
      query?: ICatalogFiltersQuery;
    }>;
    getCatalogItems: TEndpointMethod<{
      seo: string
      query?: ICatalogFiltersQuery;
    }>
  }
  account: {
    getAccountSettings: TEndpointMethod
    getAccountInfo: TEndpointMethod;
    getAccountProfile: TEndpointMethod;
    getAccountManagers: TEndpointMethod;
    getAccountCompanies: TEndpointMethod;
    getDeliveryAddress: TEndpointMethod;
    getAccountWarehouses: TEndpointMethod;
    getBalance: TEndpointMethod;
    getSearchHistory: TEndpointMethod<{
      page: number;
      query?: string;
    }>;
    getOrdersList: TEndpointMethod<{
      page?: string;
      per_page?: string;
      payer?: string;
      dateFrom?: string;
      dateTo?: string;
      query?: string;
      inn?: string;
    }>;
    getOrderInfo: TEndpointMethod<string>;
  }
  cities: {
    getSotransCities: TEndpointMethod;
  }
  contacts: {
    getCityContacts: TEndpointMethod<{
      citySlug: string;
    }>;
  }
  banners: {
    getBanners: TEndpointMethod<{type: string}>;
  }
  index: {
    getAllSections: TEndpointMethod
  }
  seo: {
    getSeo: TEndpointMethod<{
      query: {
        url: string;
      };
    }>;
  }
  brands: {
    getAllBrands: TEndpointMethod;
    getBrand: TEndpointMethod<{
      slug: string;
    }>;
    getProductCategories: TEndpointMethod<{
      query: {
        brandId: number;
      };
    }>;
    getNewProducts: TEndpointMethod;
    getBrandCompilation: TEndpointMethod<{
      query: {
        brandId: number;
      };
    }>;
    getPopularProducts: TEndpointMethod;
    getRecommendationProducts: TEndpointMethod;
    getLastSeenProducts: TEndpointMethod;
  }
  news: {
    getAllNews: TEndpointMethod<string>;
    getOneNews: TEndpointMethod<string>;
  }
  promotions: {
    getAllPromotions: TEndpointMethod<string>;
    getOnePromotion: TEndpointMethod<string>;
  }
  config: {
    getConfig: TEndpointMethod
  }
  vin: {
    getCatalogs: TEndpointMethod
    getCatalog: TEndpointMethod<{
      query: {
        vinOrFrameNumber: string
      }
    }>
    renderDetails: TEndpointMethod<{
      query: {
        c: string
        vid: string
        ssd: string
        id: string
      }
    }>
    findCatalogByWizard: TEndpointMethod<{
      query: {
        c: string
        ssd: string
      }
    }>
    findByWizard: TEndpointMethod<{
      query: {
        c: string
        ssd: string
      }
    }>
    getWizardFields: TEndpointMethod<{
      query: {
        c: string
        ssd?: string
      }
    }>
  }
  products: {
    getSeoCatalog: TEndpointMethod
    getSeoCatalogCategory: TEndpointMethod<{
      slug: string
    }>
  }
  robots: {
    getRobotsTxt: TEndpointMethod
  }
}

const serverConfig: IEndpoints = {
  cart: {
    getCartContent: () => {
      return {
        method: "GET",
        path: "/cart",
      };
    },
    getCartPreview: () => {
      return {
        method: "GET",
        path: "/cart/preview",
      };
    },
  },
  order: {
    getDeliveryAddress: () => {
      return {
        method: "GET",
        path: "/account/deliveryAddress",
      };
    },
    getDeliveryCompanies: () => {
      return {
        method: "GET",
        path: "/deliveryCompanies",
      };
    },
    getOrderProduct: (data) => {
      return {
        method: "GET",
        path: `/cart/order`,
        query: data?.query,
      };
    },
    getCityWarehouses: (data) => {
      return {
        method: "GET",
        path: `/city/${data?.citySlug}/warehouses`,
      };
    },
  },
  search: {
    searchByArticle: (data) => {
      return {
        method: "GET",
        path: `/search/${data?.article}`,
        query: data?.query,
      };
    },
    searchByArticleAndBrand: (data) => {
      return {
        method: "GET",
        path: `/search/${data?.article}/${data?.brand}`,
        query: data?.query,
      };
    },
    searchProduct: (data) => {
      return {
        method: "GET",
        path: `/search/product/${data?.productId}`,
      };
    },
  },
  catalog: {
    getInitialCategories: () => {
      return {
        method: "GET",
        path: "/catalogCategories",
      };
    },
    getCategories: (data) => {
      return {
        method: "GET",
        path: "/catalogCategories/" + data?.slug,
        query: data?.query,
      };
    },
    getCatalogItems: (data) => {
      return {
        method: 'GET',
        path: `${data?.seo}`,
        query: data?.query
      }
    }
  },
  account: {
    getAccountSettings: () => {
      return {
        method: "GET",
        path: "/account/settings"
      }
    },
    getAccountInfo: () => {
      return {
        method: "GET",
        path: "/account",
      };
    },
    getAccountProfile: () => {
      return {
        method: "GET",
        path: "/account/profile",
      };
    },
    getAccountWarehouses: () => {
      return {
        method: "GET",
        path: "/account/warehouses",
      };
    },
    getAccountCompanies: () => {
      return {
        method: "GET",
        path: "/account/company",
      };
    },
    getBalance: () => {
      return {
        method: "GET",
        path: "/account/balance",
      };
    },
    getDeliveryAddress: () => {
      return {
        method: "GET",
        path: "/account/deliveryAddress",
      };
    },
    getAccountManagers: () => {
      return {
        method: "GET",
        path: "/account/managers",
      };
    },
    getSearchHistory: (query) => {
      return {
        method: "GET",
        path: "/account/history",
        query,
      };
    },
    getOrdersList: (query) => {
      return {
        method: "GET",
        path: "/account/orders",
        query,
      };
    },
    getOrderInfo: (orderId) => {
      return {
        method: "GET",
        path: `/account/orders/${orderId}`,
      };
    },
  },
  cities: {
    getSotransCities: () => {
      return {
        method: "GET",
        path: "/cities",
      };
    },
  },
  banners: {
    getBanners: (data) => {
      return {
        method: "GET",
        path: `/banners/${data?.type}`,
      };
    },
  },
  contacts: {
    getCityContacts: (data) => {
      return {
        method: "GET",
        path: `/contacts/${data?.citySlug}`,
      };
    },
  },
  index: {
    getAllSections: () => {
      return {
        method: "GET",
        path: "/index/sections",
      };
    },
  },
  seo: {
    getSeo: (data) => {
      return {
        method: "GET",
        path: "/seo",
        query: data?.query,
      };
    },
  },
  brands: {
    getAllBrands: () => {
      return {
        method: "GET",
        path: "/brands",
      };
    },
    getBrand: (data) => {
      return {
        method: "GET",
        path: `/brands/${data?.slug}`,
      };
    },
    getProductCategories: (data) => {
      return {
        method: "GET",
        path: "/brand/categories",
        query: data?.query,
      };
    },
    getNewProducts: () => {
      return {
        method: "GET",
        path: "/index/newProducts",
      };
    },
    getBrandCompilation: (data) => {
      return {
        method: "GET",
        path: "/index/brandCompilation",
        query: data?.query,
      };
    },
    getPopularProducts: () => {
      return {
        method: "GET",
        path: "/index/popularProducts",
      };
    },
    getRecommendationProducts: () => {
      return {
        method: "GET",
        path: "/index/recommendationProducts",
      };
    },
    getLastSeenProducts: () => {
      return {
        method: "GET",
        path: "/index/lastSeenProducts",
      };
    },
  },
  news: {
    getAllNews: (numberPage) => {
      return {
        method: "GET",
        path: "/news",
        query: { page: numberPage }
      };
    },
    getOneNews: (slug) => {
      return {
        method: "GET",
        path: `/news/${slug}`,
      };
    },
  },
  promotions: {
    getAllPromotions: (numberPage) => {
      return {
        method: "GET",
        path: "/sales",
        query:  { page: numberPage }
      };
    },
    getOnePromotion: (slug) => {
      return {
        method: "GET",
        path: `/sales/${slug}`,
      };
    },
  },
  config: {
    getConfig: () => {
      return {
        method: "GET",
        path: "/config/getConfig",
      }
    },
  },
  vin: {
    getCatalogs: () => {
      return {
        method: "GET",
        path: "/vin/getCatalogs",
      }
    },
    getCatalog: (data) => {
      return {
        method: "GET",
        path: "/vin/findVehicle",
        query: data?.query,
      }
    },
    renderDetails: (data) => {
      return {
        method: "GET",
        path: "/vin/renderDetails",
        query: data?.query
      }
    },
    findCatalogByWizard: (data) => {
      return {
        method: "GET",
        path: "/vin/findCatalogByWizard",
        query: data?.query
      }
    },
    findByWizard: (data) => {
      return {
        method: "GET",
        path: "/vin/findByWizard",
        query: data?.query
      }
    },
    getWizardFields: (data) => {
      return {
        method: "GET",
        path: "/vin/getWizardFields",
        query: data?.query
      }
    }
  },
  products: {
    getSeoCatalog: () => {
      return {
        method: 'GET',
        path: '/products'
      }
    },
    getSeoCatalogCategory: (data) => {
      return {
        method: 'GET',
        path: `/products/${data?.slug}`
      }
    }
  },
  robots: {
    getRobotsTxt: () => {
      return {
        method: 'GET',
        path: '/robots'
      }
    }
  }
};

export default serverConfig;
